import { renameFile } from "@msuite/utils/files";
import { guardNumber } from "@msuite/utils/numbers";
import type { Tables, UploadResult } from "@msuite/utils/types";
import { useEffect, useState } from "react";
import { scrollIntoView } from "../utils";

/** Props Interface */
export interface UseFileUploadProps {
	defaultFiles?: Array<Tables<"files">> | (() => Promise<Array<Tables<"files">>>);
	getRemoteFileUrl?: (file: Tables<"files">) => Promise<string | null>;
	onFilesChange?: (files: File[]) => void | Promise<void>;
	onComplete?: (files: UploadResult[]) => void | Promise<void>;
	resetOnComplete?: boolean;
	uploadFn: (formData: FormData) => Promise<UploadResult[] | null>;
}

export type FileUploadController = ReturnType<typeof useFileUpload>;

export function useFileUpload(props: UseFileUploadProps | undefined) {
	/** State */
	const [defaultFiles, setDefaultFiles] = useState<Tables<"files">[]>([]);
	const [defaultFileUrl, setDefaultFileUrl] = useState<string | null>(null);
	const [files, setFiles] = useState<File[]>([]);
	const [isUploading, setIsUploading] = useState<boolean>(false);
	const [isUploaded, setIsUploaded] = useState<boolean>(false);
	const [isFetchingDefaultFiles, setIsFetchingDefaultFiles] = useState<boolean>(false);
	const [isFetchingDefaultFileUrl, setIsFetchingDefaultFileUrl] = useState<boolean>(false);

	/** Functions */
	async function handleOnFileUpload(injectedFiles: File[] = []) {
		try {
			const combinedFiles = [...files, ...injectedFiles];
			if (combinedFiles.length < 1) return [] as UploadResult[];
			setIsUploading(true);

			const form = new FormData();

			for (const file of combinedFiles) {
				const _renamedFile = renameFile(file, (file as any).path);
				form.append("files", _renamedFile);
				scrollIntoView({
					id: file.name,
				});
			}

			const results = await props?.uploadFn(form);
			await props?.onComplete?.(results || []);
			setIsUploaded(true);
			if (props?.resetOnComplete) handleOnReset();
			return (results || []) as UploadResult[];
		} catch (error) {
			console.error(error);
			return [] as UploadResult[];
		} finally {
			setIsUploading(false);
		}
	}

	async function handleOnAddFiles(files: File[]) {
		await props?.onFilesChange?.(files);
		setFiles((currentFiles) => [...currentFiles, ...files]);
	}

	async function handleOnSetFiles(files: File[]) {
		await props?.onFilesChange?.(files);
		setFiles(files);
	}

	function handleOnAddDefaultFiles(addedFiles: Tables<"files">[]) {
		setDefaultFiles((currentFiles) => [
			...currentFiles,
			...addedFiles.filter((file) => !currentFiles.some((current) => current.id === file.id)),
		]);
	}

	function handleOnReset() {
		setFiles([]);
		setIsUploaded(false);
	}

	function handleOnRemove(file: File) {
		setFiles((files) => files.filter((f) => f !== file));
	}

	function getFilesBase64() {
		return files.map((file) => {
			return new Promise<string>((resolve) => {
				const reader = new FileReader();
				reader.onload = () => {
					resolve(reader.result as string);
				};
				reader.readAsDataURL(file);
			});
		});
	}

	/** Effects */
	useEffect(() => {
		async function handleOnCreateDefaultFiles() {
			setIsFetchingDefaultFiles(true);
			await createDefaultFiles(props?.defaultFiles).then(setDefaultFiles);
			setIsFetchingDefaultFiles(false);
		}
		handleOnCreateDefaultFiles();
	}, []);

	useEffect(() => {
		async function handleOnResolveDefaultFileUrl() {
			const firstDefaultFile = defaultFiles[0];
			if (!firstDefaultFile || !props?.getRemoteFileUrl) {
				setDefaultFileUrl(null);
				return;
			}
			setIsFetchingDefaultFileUrl(true);
			const url = await props.getRemoteFileUrl(firstDefaultFile);
			setDefaultFileUrl(url);
			setIsFetchingDefaultFileUrl(false);
		}
		handleOnResolveDefaultFileUrl();
	}, [defaultFiles]);

	/** Constants */
	const hasFiles = files.length > 0;
	const length = guardNumber(files.length) + guardNumber(defaultFiles.length);

	/** Return */
	return {
		files,
		handleOnAddFiles,
		handleOnSetFiles,
		handleOnFileUpload,
		handleOnRemove,
		handleOnReset,
		handleOnAddDefaultFiles,
		isUploading,
		isUploaded,
		hasFiles,
		getFilesBase64,
		isFetchingDefaultFiles,
		defaultFiles,
		defaultFileUrl,
		isFetchingDefaultFileUrl,
		length,
	};
}
async function createDefaultFiles(defaultFiles: UseFileUploadProps["defaultFiles"]) {
	if (!defaultFiles) return [];
	if (Array.isArray(defaultFiles)) return defaultFiles as Tables<"files">[];
	if (typeof defaultFiles === "function") {
		const files = await defaultFiles();
		return files;
	}
	if (typeof defaultFiles === "object") {
		return [defaultFiles];
	}
	return [];
}
